import { useState, useEffect, useCallback } from 'react'
import { Row, Col, Card, Table, Badge, Spinner } from 'react-bootstrap'
import { extensionsApi, trunksApi, cdrApi, queuesApi, dashboardApi } from '../services/api'
import { useAuthStore } from '../store/authStore'

interface Stats {
  extensions: number
  trunks: number
  queues: number
  totalCalls: number
  answeredCalls: number
  missedCalls: number
  answerRate: number
  avgDuration: number
}

interface RecentCall {
  id: string
  calldate: string
  src: string
  src_name: string | null
  src_type: string | null
  dst: string
  dst_name: string | null
  dst_type: string | null
  clid: string
  billsec: number
  disposition: string
  direction: string
}

interface ActiveCall {
  channel: string
  caller_id_num: string
  caller_id_name: string
  connected_line_num: string
  connected_line_name: string
  duration: string
  state: string
  application: string
  direction: string
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    extensions: 0,
    trunks: 0,
    queues: 0,
    totalCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    answerRate: 0,
    avgDuration: 0
  })
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([])
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([])
  const [extensionLookup, setExtensionLookup] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [activeCallsLoading, setActiveCallsLoading] = useState(false)
  const { user, selectedTenant } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'

  const loadActiveCalls = useCallback(async () => {
    try {
      setActiveCallsLoading(true)
      const res = await dashboardApi.getActiveCalls()
      setActiveCalls(res.data.calls || [])
    } catch (error) {
      console.error('Error loading active calls:', error)
    } finally {
      setActiveCallsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStats()
    loadActiveCalls()

    // Poll active calls every 3 seconds
    const interval = setInterval(loadActiveCalls, 3000)
    return () => clearInterval(interval)
  }, [selectedTenant, loadActiveCalls])

  const loadStats = async () => {
    setLoading(true)
    try {
      const params = isSuperAdmin && selectedTenant ? { tenant_id: selectedTenant.id } : {}

      const [extRes, trunkRes, queueRes, cdrStatsRes, cdrRes, lookupRes] = await Promise.all([
        extensionsApi.list({ limit: 1, ...params }),
        trunksApi.list({ limit: 1, ...params }),
        queuesApi.list({ limit: 1, ...params }).catch(() => ({ data: { total: 0 } })),
        cdrApi.stats(params),
        cdrApi.list({ limit: 10, ...params }),
        dashboardApi.getExtensionLookup()
      ])

      setStats({
        extensions: extRes.data.total || 0,
        trunks: trunkRes.data.total || 0,
        queues: queueRes.data.total || 0,
        totalCalls: cdrStatsRes.data.total_calls || 0,
        answeredCalls: cdrStatsRes.data.answered_calls || 0,
        missedCalls: cdrStatsRes.data.missed_calls || 0,
        answerRate: cdrStatsRes.data.answer_rate || 0,
        avgDuration: cdrStatsRes.data.average_duration_seconds || 0
      })

      setRecentCalls(cdrRes.data.items || [])
      setExtensionLookup(lookupRes.data || {})
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    const mins = Math.floor(seconds / 60)
    const secs = Math.round(seconds % 60)
    return `${mins}m ${secs}s`
  }

  const getDispositionBadge = (disposition: string) => {
    switch (disposition) {
      case 'ANSWERED': return <Badge bg="success">Angenommen</Badge>
      case 'BUSY': return <Badge bg="warning">Besetzt</Badge>
      case 'NO ANSWER': return <Badge bg="danger">Verpasst</Badge>
      default: return <Badge bg="secondary">{disposition}</Badge>
    }
  }

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound': return <i className="bi bi-telephone-inbound text-success me-1" title="Eingehend"></i>
      case 'outbound': return <i className="bi bi-telephone-outbound text-primary me-1" title="Ausgehend"></i>
      case 'internal': return <i className="bi bi-arrow-left-right text-secondary me-1" title="Intern"></i>
      default: return null
    }
  }

  const getActiveCallStateBadge = (state: string) => {
    switch (state.toLowerCase()) {
      case 'up': return <Badge bg="success">Verbunden</Badge>
      case 'ringing': return <Badge bg="warning">Klingelt</Badge>
      case 'ring': return <Badge bg="warning">Klingelt</Badge>
      case 'dialing': return <Badge bg="info">Wählt</Badge>
      default: return <Badge bg="secondary">{state}</Badge>
    }
  }

  // Resolve extension number to name
  const resolveExtensionName = (num: string, name?: string): string => {
    if (name && name !== num) return name
    if (extensionLookup[num]) return extensionLookup[num]
    return ''
  }

  // Format destination with name
  const formatDestination = (num: string, name?: string): JSX.Element => {
    const resolvedName = resolveExtensionName(num, name)
    if (resolvedName && resolvedName !== num) {
      return (
        <>
          <strong>{num}</strong>
          <div className="small text-muted">{resolvedName}</div>
        </>
      )
    }
    return <strong>{num}</strong>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-speedometer2 me-2"></i>
          Dashboard
        </h4>
        {selectedTenant && (
          <Badge bg="primary" className="px-3 py-2">
            <i className="bi bi-building me-1"></i>
            {selectedTenant.name}
          </Badge>
        )}
      </div>

      {/* Stats Row 1 - System */}
      <Row className="mb-3">
        <Col md={3}>
          <Card className="h-100 border-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-primary">{stats.extensions}</div>
                  <small className="text-muted">Nebenstellen</small>
                </div>
                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-telephone fs-4 text-primary"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-info">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-info">{stats.trunks}</div>
                  <small className="text-muted">Trunks</small>
                </div>
                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-diagram-3 fs-4 text-info"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-secondary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-secondary">{stats.queues}</div>
                  <small className="text-muted">Warteschlangen</small>
                </div>
                <div className="bg-secondary bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-people fs-4 text-secondary"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-warning">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-warning">{stats.answerRate.toFixed(0)}%</div>
                  <small className="text-muted">Annahmerate</small>
                </div>
                <div className="bg-warning bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-pie-chart fs-4 text-warning"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stats Row 2 - Calls */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0">{stats.totalCalls}</div>
                  <small className="text-muted">Anrufe (30 Tage)</small>
                </div>
                <div className="bg-light rounded-circle p-3">
                  <i className="bi bi-graph-up fs-4 text-dark"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-success">{stats.answeredCalls}</div>
                  <small className="text-muted">Angenommen</small>
                </div>
                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-telephone-inbound fs-4 text-success"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0 text-danger">{stats.missedCalls}</div>
                  <small className="text-muted">Verpasst</small>
                </div>
                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                  <i className="bi bi-telephone-x fs-4 text-danger"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="h2 mb-0">{formatDuration(stats.avgDuration)}</div>
                  <small className="text-muted">Ø Gesprächsdauer</small>
                </div>
                <div className="bg-light rounded-circle p-3">
                  <i className="bi bi-clock fs-4 text-dark"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Active Calls Section */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-success">
            <Card.Header className="d-flex justify-content-between align-items-center bg-success bg-opacity-10">
              <span>
                <i className="bi bi-telephone-fill text-success me-2"></i>
                Aktive Anrufe
                <Badge bg="success" className="ms-2">{activeCalls.length}</Badge>
              </span>
              {activeCallsLoading && (
                <Spinner animation="border" size="sm" variant="success" />
              )}
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Von</th>
                    <th>Nach</th>
                    <th>Dauer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCalls.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        <i className="bi bi-telephone-x me-2"></i>
                        Keine aktiven Anrufe
                      </td>
                    </tr>
                  ) : (
                    activeCalls.map((call, idx) => (
                      <tr key={idx}>
                        <td>
                          {getDirectionIcon(call.direction)}
                          <strong>{call.caller_id_num}</strong>
                          {call.caller_id_name && call.caller_id_name !== call.caller_id_num && (
                            <div className="small text-muted">{call.caller_id_name}</div>
                          )}
                        </td>
                        <td>
                          {formatDestination(call.connected_line_num, call.connected_line_name)}
                        </td>
                        <td>
                          <i className="bi bi-clock me-1 text-muted"></i>
                          {call.duration}
                        </td>
                        <td>{getActiveCallStateBadge(call.state)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-clock-history me-2"></i>
                Letzte Anrufe
              </span>
              <a href="/cdr" className="btn btn-sm btn-outline-primary">
                Alle anzeigen <i className="bi bi-arrow-right ms-1"></i>
              </a>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Von</th>
                    <th>Nach</th>
                    <th>Dauer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Laden...
                      </td>
                    </tr>
                  ) : recentCalls.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        <i className="bi bi-inbox me-2"></i>
                        Keine Anrufe vorhanden
                      </td>
                    </tr>
                  ) : (
                    recentCalls.map((c) => (
                      <tr key={c.id}>
                        <td className="small">
                          {new Date(c.calldate).toLocaleString('de-CH', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td>
                          {getDirectionIcon(c.direction)}
                          <strong>{c.src}</strong>
                          {(c.src_name || (c.clid && c.clid !== c.src)) && (
                            <div className="small text-muted">
                              {c.src_name || c.clid?.replace(/<.*>/, '').replace(/"/g, '').trim()}
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>{c.dst_name || c.dst}</strong>
                          {c.dst_name && c.dst_name !== c.dst && c.dst !== 's' && (
                            <span className="small text-muted ms-1">({c.dst})</span>
                          )}
                        </td>
                        <td>{formatDuration(c.billsec)}</td>
                        <td>{getDispositionBadge(c.disposition)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <i className="bi bi-person-circle me-2"></i>
              Benutzer
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <small className="text-muted d-block">Benutzername</small>
                <strong>{user?.username}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Rolle</small>
                <Badge bg={user?.role === 'super_admin' ? 'danger' : user?.role === 'tenant_admin' ? 'warning' : 'secondary'}>
                  {user?.role === 'super_admin' && 'Super Administrator'}
                  {user?.role === 'tenant_admin' && 'Mandanten-Admin'}
                  {user?.role === 'user' && 'Benutzer'}
                </Badge>
              </div>
              {selectedTenant && (
                <div>
                  <small className="text-muted d-block">Mandant</small>
                  <strong>{selectedTenant.name}</strong>
                  <div className="small text-muted">{selectedTenant.domain}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <i className="bi bi-info-circle me-2"></i>
              System
            </Card.Header>
            <Card.Body>
              <div className="mb-2 d-flex justify-content-between">
                <small className="text-muted">Version</small>
                <strong>it64Pbx 1.0.0</strong>
              </div>
              <div className="d-flex justify-content-between">
                <small className="text-muted">Asterisk</small>
                <strong>PJSIP</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
